import type { Place } from '../types/Place';

export interface MonthMarker {
  date: Date;
  label: string;
  position: number; // 0-100%
}

/**
 * Filter places that should appear in Time Machine (excludes wishlist)
 */
export function getTimelinePlaces(places: Place[]): Place[] {
  return places.filter(place => place.layer !== 'wishlist');
}

/**
 * Parse dateSort string (e.g., "2025-01-14") to Date object
 */
export function parseDateSort(dateSort: string): Date {
  return new Date(dateSort);
}

/**
 * Get the date range from places (start and end dates in milliseconds)
 */
export function getDateRange(places: Place[]): { startMs: number; endMs: number; durationMs: number } {
  const timelinePlaces = getTimelinePlaces(places);

  if (timelinePlaces.length === 0) {
    const now = Date.now();
    return { startMs: now, endMs: now, durationMs: 0 };
  }

  const dates = timelinePlaces.map(p => parseDateSort(p.dateSort).getTime());
  const startMs = Math.min(...dates);
  const endMs = Math.max(...dates);

  return { startMs, endMs, durationMs: endMs - startMs };
}

/**
 * Calculate which places should be visible at a given time
 */
export function getVisiblePlacesAtTime(places: Place[], currentTimeMs: number): Set<string> {
  const visible = new Set<string>();
  const timelinePlaces = getTimelinePlaces(places);

  for (const place of timelinePlaces) {
    const placeTime = parseDateSort(place.dateSort).getTime();
    if (placeTime <= currentTimeMs) {
      visible.add(place.id);
    }
  }

  return visible;
}

/**
 * Convert progress (0-100) to actual time in milliseconds
 */
export function progressToTime(progress: number, startMs: number, durationMs: number): number {
  return startMs + (progress / 100) * durationMs;
}

/**
 * Convert time in milliseconds to progress (0-100)
 */
export function timeToProgress(timeMs: number, startMs: number, durationMs: number): number {
  if (durationMs === 0) return 100;
  return Math.max(0, Math.min(100, ((timeMs - startMs) / durationMs) * 100));
}

/**
 * Generate month markers for the timeline
 */
export function generateMonthMarkers(places: Place[]): MonthMarker[] {
  const { startMs, endMs, durationMs } = getDateRange(places);

  if (durationMs === 0) return [];

  const markers: MonthMarker[] = [];
  const startDate = new Date(startMs);
  const endDate = new Date(endMs);

  // Start from the first day of the start month
  const currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

  while (currentDate <= endDate) {
    const timeMs = currentDate.getTime();
    // Only add markers that fall within the actual range
    if (timeMs >= startMs) {
      const position = timeToProgress(timeMs, startMs, durationMs);
      const label = formatMonthLabel(currentDate, startDate, endDate);

      markers.push({
        date: new Date(currentDate),
        label,
        position: Math.max(0, Math.min(100, position)),
      });
    }

    // Move to next month
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return markers;
}

/**
 * Format month label based on context (show year if spans multiple years)
 */
function formatMonthLabel(date: Date, startDate: Date, endDate: Date): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];

  // Show year suffix if the timeline spans multiple years
  const spansYears = startDate.getFullYear() !== endDate.getFullYear();
  if (spansYears) {
    const yearSuffix = String(date.getFullYear()).slice(-2);
    return `${month} '${yearSuffix}`;
  }

  return month;
}

/**
 * Format current date for display (e.g., "March 2025")
 */
export function formatCurrentDate(timeMs: number): string {
  const date = new Date(timeMs);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return `${months[date.getMonth()]} ${date.getFullYear()}`;
}
